import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from './States';
import { dateTime } from '../lib/format';
import type { CredentialsPreview, CredentialsStatus, TokenInfo } from '../api/types';

/** Quanto falta para o token vencer, em linguagem de gente. */
function prazoDoToken(t: TokenInfo): { texto: string; tom: 'ok' | 'warn' | 'error' } {
  if (t.unreadable) return { texto: 'a validade não é legível neste token', tom: 'warn' };
  if (t.expiresAt === null) return { texto: 'o token não declara validade', tom: 'warn' };
  if (t.expired) return { texto: `venceu em ${dateTime(t.expiresAt)}`, tom: 'error' };

  const dias = t.daysLeft ?? 0;
  const quando = dateTime(t.expiresAt);
  if (dias === 0) return { texto: `vence hoje (${quando})`, tom: 'error' };
  if (dias <= 3) return { texto: `${dias} ${dias === 1 ? 'dia' : 'dias'} para vencer (${quando})`, tom: 'error' };
  if (dias <= 7) return { texto: `${dias} dias para vencer — ${quando}`, tom: 'warn' };
  return { texto: `${dias} dias para vencer — ${quando}`, tom: 'ok' };
}

const CAMPOS_MANUAIS = ['authorization', 'uk', 'deviceuk', 'deviceid', 'appVersion'] as const;

/**
 * Troca das credenciais do Youpin.
 *
 * O caminho principal é colar o "Copy as cURL" do DevTools: pedir cinco campos
 * separados é onde se erra, e um campo errado derruba o catálogo inteiro. Os
 * campos individuais ficam escondidos, para quando só um precisa mudar.
 */
export function CredentialsPanel() {
  const [status, setStatus] = useState<CredentialsStatus | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [colado, setColado] = useState('');
  const [previa, setPrevia] = useState<CredentialsPreview | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [manual, setManual] = useState(false);
  const [campos, setCampos] = useState<Record<string, string>>({});

  const carregar = useCallback(async () => {
    try {
      setStatus(await api.adminCredentials());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui ler o status das credenciais.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // A prévia roda enquanto se digita, mas não salva nada: mostra o que foi
  // entendido antes de substituir credenciais que estão funcionando.
  useEffect(() => {
    if (!colado.trim()) {
      setPrevia(null);
      return;
    }
    const timer = window.setTimeout(() => {
      void api
        .adminPreviewCredentials(colado)
        .then(setPrevia)
        .catch(() => setPrevia(null));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [colado]);

  const nadaParaEnviar = !colado.trim() && Object.values(campos).every((v) => !v.trim());

  async function aplicar() {
    setSalvando(true);
    setErro(null);
    setOk(null);
    try {
      const preenchidos = Object.fromEntries(Object.entries(campos).filter(([, v]) => v.trim()));
      const res = await api.adminApplyCredentials({
        pasted: colado.trim() || undefined,
        fields: Object.keys(preenchidos).length ? preenchidos : undefined,
      });
      setOk(`Credenciais atualizadas (${res.aplicados.join(', ')}). O cache foi limpo.`);
      setColado('');
      setCampos({});
      setPrevia(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar as credenciais.');
    } finally {
      setSalvando(false);
    }
  }

  if (erro && !status) return <Banner kind="error">{erro}</Banner>;
  if (!status) return <Spinner label="Lendo as credenciais…" />;

  const prazo = prazoDoToken(status.token);

  return (
    <div className="panel">
      <h2>Credenciais do Youpin</h2>

      {status.rejectedSince ? (
        <Banner kind="error">
          O Youpin passou a recusar as chamadas em {dateTime(status.rejectedSince)}. Cole um cURL novo abaixo — o
          catálogo volta assim que as credenciais forem trocadas.
        </Banner>
      ) : (
        <Banner kind={prazo.tom}>
          Credenciais em uso: {prazo.texto}.
          {status.updatedAt ? ` Trocadas em ${dateTime(status.updatedAt)}.` : ''}
        </Banner>
      )}

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <div className="stat-row">
        <span>
          vence em: <b>{status.token.expiresAt ? dateTime(status.token.expiresAt) : '—'}</b>
        </span>
        {status.token.daysLeft !== null && (
          <span className={status.token.daysLeft <= 7 ? 'warn' : ''}>
            faltam <b>{status.token.daysLeft}</b> dia(s)
          </span>
        )}
        {status.token.issuedAt && (
          <span>
            emitido em: <b>{dateTime(status.token.issuedAt)}</b>
          </span>
        )}
        <span>
          campos completos: <b>{status.complete ? 'sim' : 'não'}</b>
        </span>
        {status.listingBodyVariant && (
          <span className="dim">
            formato de anúncios aceito: <b>{status.listingBodyVariant}</b>
          </span>
        )}
      </div>

      <h3>Trocar credenciais</h3>
      <p className="muted">
        Com a sua conta do Youpin aberta no navegador: <b>F12</b> → aba <b>Network</b> → botão direito em qualquer
        chamada para o Youpin → <b>Copy as cURL</b>. Cole tudo aqui embaixo; os campos são separados
        automaticamente.
      </p>

      <textarea
        className="paste-box"
        rows={6}
        value={colado}
        onChange={(e) => setColado(e.target.value)}
        placeholder={'curl "https://api.youpin898.com/..." -H "authorization: eyJ0eX..." -H "uk: ..." ...'}
        spellCheck={false}
      />

      {previa && (
        <div className="paste-preview">
          {previa.encontrados.length === 0 ? (
            <span className="neg">Não reconheci nenhuma credencial nesse texto.</span>
          ) : (
            <>
              <span className="pos">Reconhecido: {previa.encontrados.join(', ')}.</span>
              {previa.token?.expiresAt && !previa.token.expired && (
                <span className="dim"> O token novo vence em {dateTime(previa.token.expiresAt)}.</span>
              )}
              {previa.token?.expired && <span className="neg"> Atenção: esse token já está vencido.</span>}
            </>
          )}
        </div>
      )}

      <div className="actions-row">
        <button className="btn primary" onClick={() => void aplicar()} disabled={salvando || nadaParaEnviar}>
          {salvando ? 'Aplicando…' : 'Aplicar credenciais'}
        </button>
        <button className="btn ghost" onClick={() => setManual((v) => !v)}>
          {manual ? 'Esconder campos' : 'Preencher campo a campo'}
        </button>
      </div>

      {manual && (
        <div className="form">
          {CAMPOS_MANUAIS.map((campo) => (
            <label className="field" key={campo}>
              <span>{campo}</span>
              <input
                value={campos[campo] ?? ''}
                onChange={(e) => setCampos((prev) => ({ ...prev, [campo]: e.target.value }))}
                placeholder="deixe vazio para manter o valor atual"
                spellCheck={false}
              />
            </label>
          ))}
        </div>
      )}

      <h3>Valores em uso</h3>
      <p className="muted">Mascarados de propósito: dá para conferir se mudaram, sem expor o token na tela.</p>
      <pre className="code">{JSON.stringify(status.credentials, null, 2)}</pre>
    </div>
  );
}
